import { Chip, SxProps } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { RowGroupingColumnTableCell } from '../../row-grouping/components/RowGroupingColumnTableCell'
import { BaseColumnConfig } from '../types'
import { baseColumnConfig } from './baseColumnConfig'
import { ColumnHeaderWithTooltip } from './helpers/ColumnHeaderWithTooltip'
import type { ChipProps } from '@mui/material'
import type { GridValidRowModel } from '@mui/x-data-grid/models/gridRows'

export type ChipSingleColumnConfig<T extends GridValidRowModel> = BaseColumnConfig<T, 'chip-single'> & {
  columnType: 'chip-single'
  getChipSx?: (row: T) => SxProps
  getColor?: (row: T) => ChipProps['color']
  getTitle?: (row: T) => string
  headerTooltip?: string
  valueGetter: GridColDef<T>['valueGetter']
  // Group-header display only: map the raw group key to a label. The key stays raw (it round-trips to the
  // backend on expand), so the label transform has to happen here at render, not in the grouping value.
  formatGroupValue?: (value: string) => string
  // Cell display only, and for the same reason as formatGroupValue: a column whose values are a
  // closed set shows a label while the cell value stays the raw key that filters and sorting carry.
  formatValue?: (value: string) => string
}

export const getChipColumn = <T extends GridValidRowModel>({
  columnType,
  style,
  formatGroupValue,
  formatValue,
  getTitle,
  headerTooltip,
  ...config
}: ChipSingleColumnConfig<T>): GridColDef<T> => ({
  ...baseColumnConfig,
  ...style,
  ...config,
  ...(headerTooltip !== undefined && {
    renderHeader: () => <ColumnHeaderWithTooltip label={config.headerName} tooltip={headerTooltip} />,
  }),
  groupingValueGetter: config.valueGetter,
  renderCell: (params) => {
    if (!isDefined(params.value)) return null
    if (params.rowNode.type === 'group') {
      const value = formatGroupValue ? formatGroupValue(params.value) : params.value
      return <RowGroupingColumnTableCell columnHeaderName={config.headerName} value={value} id={params.id} />
    }
    if (params.value.length === 0) return null

    const chipSx = config.getChipSx ? config.getChipSx(params.row) : {}
    const color = config.getColor ? config.getColor(params.row) : 'neutral'
    const label = formatValue ? formatValue(params.value) : params.value

    return (
      <Chip
        {...(config?.getDataTestId ? { ['data-testid']: config.getDataTestId(params) } : {})}
        sx={chipSx}
        label={label}
        color={color}
        title={getTitle ? getTitle(params.row) : label}
      />
    )
  },
})
